import { useState, useCallback, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faLock } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { workosAuth } from "@/services/api/workos";
import { Button } from "@/components/ui/button/Button";
import { OtpCodeInput } from "@/components/auth/OtpCodeInput";
import { useNotification } from "@/context/NotificationContext";

export interface MfaEnrollDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export function MfaEnrollDialog({ isOpen, onClose }: MfaEnrollDialogProps) {
  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = useState(false);
  const [enrolled, setEnrolled] = useState(false);
  const [available, setAvailable] = useState(true);
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [totpUri, setTotpUri] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setLoading(false);
    setChallengeId(null);
    setTotpUri(null);
    setCode("");
    setLocalError(null);
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    setLocalError(null);
    workosAuth
      .getMfaStatus()
      .then((status) => {
        if (cancelled) return;
        setAvailable(status.available);
        setEnrolled(status.enrolled);
      })
      .catch((err) => {
        if (cancelled) return;
        setLocalError(err instanceof Error ? err.message : "Could not load MFA status");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleStart = async () => {
    setLocalError(null);
    setLoading(true);
    try {
      const result = await workosAuth.startMfaEnrollment();
      setChallengeId(result.authentication_challenge_id);
      setTotpUri(result.totp_uri ?? null);
    } catch (err) {
      showError("Could not start MFA", err instanceof Error ? err.message : "Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!challengeId) return;
    setLocalError(null);
    setLoading(true);
    try {
      await workosAuth.confirmMfaEnrollment(code.trim(), challengeId);
      showSuccess("MFA enabled", "You will be asked for a code the next time you sign in.");
      reset();
      onClose();
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : "Invalid authentication code");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      data-testid="mfa-enroll-dialog-backdrop"
    >
      <div
        className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl dark:bg-gray-800"
        data-testid="mfa-enroll-dialog"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
            <FontAwesomeIcon icon={faLock} />
            Authenticator app
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
            data-testid="mfa-enroll-dialog-close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {localError ? (
          <p className="mb-3 text-sm text-red-600 dark:text-red-400" data-testid="mfa-enroll-dialog-error">
            {localError}
          </p>
        ) : null}

        {loading && !challengeId && !enrolled ? (
          <p className="text-sm text-gray-600 dark:text-gray-300">Loading…</p>
        ) : !available ? (
          <p className="text-sm text-gray-600 dark:text-gray-300">
            MFA is not available for this account.
          </p>
        ) : enrolled && !challengeId ? (
          <p className="text-sm text-gray-600 dark:text-gray-300" data-testid="mfa-enrolled-message">
            Authenticator app is on. Lost your device? Ask an admin to reset MFA in WorkOS.
          </p>
        ) : challengeId ? (
          <form onSubmit={handleConfirm} className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Add this account in your authenticator app, then enter the 6-digit code.
            </p>
            {totpUri ? (
              <p className="break-all rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-700 dark:bg-gray-700 dark:text-gray-200" data-testid="mfa-enroll-totp-uri">
                {totpUri}
              </p>
            ) : null}
            <label
              htmlFor="mfa-enroll-code"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Authentication code
            </label>
            <OtpCodeInput
              id="mfa-enroll-code"
              value={code}
              onChange={setCode}
              disabled={loading}
              autoFocus
              data-testid="mfa-enroll-code-input"
            />
            <div className="flex justify-end gap-2">
              <Button type="button" outline onClick={handleClose} data-testid="mfa-enroll-cancel">
                Cancel
              </Button>
              <Button type="submit" color="indigo" disabled={loading || code.length < 6} data-testid="mfa-enroll-confirm">
                Enable MFA
              </Button>
            </div>
          </form>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Require a 6-digit code from an authenticator app when you sign in with email and password.
            </p>
            <div className="flex justify-end gap-2">
              <Button type="button" outline onClick={handleClose}>
                Close
              </Button>
              <Button type="button" color="indigo" onClick={handleStart} disabled={loading} data-testid="mfa-enroll-start">
                Set up authenticator
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
